import React from 'react';
import {
    Paper,
    Typography,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
} from '@material-ui/core';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';

const toNumberOrNull = (value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
};

const normalize = (text) => (typeof text === 'string' ? text.trim().toLowerCase() : '');
const stripDiacritics = (text) =>
    typeof text === 'string' ? text.normalize('NFD').replace(/[\u0300-\u036f]/g, '') : '';

const getOptionFromQuestion = (question, response) => {
    if (!question) return null;
    const refId = Number(response?.scaleOptionId);
    return (question.questionOptionScores || []).find((opt) => {
        const optId = opt.scaleOptionId || opt.scaleOption?.id || opt.id;
        return Number(optId) === refId;
    });
};

const interpretEducationLevel = (register, scale) => {
    const allQuestions = Array.isArray(scale?.questions) ? scale.questions : [];
    const responses = Array.isArray(register?.scaleResponses) ? register.scaleResponses : [];
    const questionById = new Map(allQuestions.map((q) => [String(q.id), q]));

    for (const response of responses) {
        const question = questionById.get(String(response?.scaleQuestionId));
        if (!question) continue;
        const possibleFields = [
            question?.code,
            question?.questionCode,
            question?.shortCode,
            question?.shortName,
            question?.name,
            question?.label,
        ];
        const isEducationQuestion = possibleFields.some((value) => {
            if (typeof value !== 'string') return false;
            const normalized = stripDiacritics(value.toLowerCase());
            return (
                normalized.includes('education') ||
                normalized.includes('educacion') ||
                normalized.includes('educativo') ||
                normalized.includes('nivel educativo')
            );
        });
        if (!isEducationQuestion) continue;

        const option = getOptionFromQuestion(question, response);
        const label =
            option?.scaleOption?.label ||
            option?.label ||
            response?.textValue ||
            response?.value ||
            response?.numericValue;
        if (typeof label === 'string' && label.trim()) {
            return label.trim();
        }
    }
    return null;
};

const deriveErrorFromResponse = (response, question) => {
    const direct = toNumberOrNull(response?.score);
    if (direct != null) return direct ? 1 : 0;
    const numeric = toNumberOrNull(response?.numericValue);
    if (numeric != null) return numeric ? 1 : 0;
    const option = getOptionFromQuestion(question, response);
    if (option) {
        const optScore = toNumberOrNull(option?.score);
        if (optScore != null) return optScore ? 1 : 0;
        const label = option?.scaleOption?.label || option?.label || option?.value;
        const normalized = stripDiacritics(normalize(label));
        if (normalized === 'incorrecto' || normalized === 'incorrecta') return 1;
        if (normalized === 'correcto' || normalized === 'correcta') return 0;
    }
    const fallbackLabel = stripDiacritics(normalize(response?.textValue));
    if (fallbackLabel === 'incorrecto' || fallbackLabel === 'incorrecta') return 1;
    if (fallbackLabel === 'correcto' || fallbackLabel === 'correcta') return 0;
    return null;
};

const getCutoffsByEducation = (educationLevel) => {
    const normalized = stripDiacritics(normalize(educationLevel));
    if (normalized.includes('universitario') || normalized.includes('alto')) {
        return { normal: 1, mild: 3, moderate: 6 };
    }
    if (normalized.includes('elemental') || normalized.includes('bajo')) {
        return { normal: 3, mild: 5, moderate: 8 };
    }
    return { normal: 2, mild: 4, moderate: 7 };
};

const getAssessmentFromErrors = (totalErrors, cutoffs) => {
    if (totalErrors <= cutoffs.normal) return 'Valoración cognitiva normal';
    if (totalErrors <= cutoffs.mild) return 'Deterioro leve';
    if (totalErrors <= cutoffs.moderate) return 'Deterioro moderado';
    return 'Deterioro severo';
};

const computeSpmsqResult = (register, scale, overrideEducationLevel) => {
    if (!register || !scale) return null;
    const responses = Array.isArray(register?.scaleResponses) ? register.scaleResponses : [];
    const questions = Array.isArray(scale?.questions) ? scale.questions : [];
    if (!responses.length || !questions.length) return null;

    const questionById = new Map(questions.map((q) => [String(q.id), q]));
    const educationLevel = overrideEducationLevel || interpretEducationLevel(register, scale);
    const cutoffs = getCutoffsByEducation(educationLevel || 'medio');

    let totalErrors = 0;
    responses.forEach((response) => {
        const question = questionById.get(String(response?.scaleQuestionId));
        if (!question) return;
        const error = deriveErrorFromResponse(response, question);
        if (error == null) return;
        totalErrors += error ? 1 : 0;
    });

    const assessment = getAssessmentFromErrors(totalErrors, cutoffs);

    const interpretationItems = [
        { label: `0 - ${cutoffs.normal} errores: Valoración cognitiva normal` },
        { label: `${cutoffs.normal + 1} - ${cutoffs.mild} errores: Deterioro leve` },
        { label: `${cutoffs.mild + 1} - ${cutoffs.moderate} errores: Deterioro moderado` },
        { label: `${cutoffs.moderate + 1} - 10 errores: Deterioro severo` },
    ];

    return {
        totalErrors,
        cognitiveAssessment: assessment,
        interpretationItems,
        educationLevel: educationLevel || 'Medio',
    };
};

export const ScaleResultSPMSQ = ({
    title = 'Resultados',
    totalErrors,
    maxErrors = 10,
    cognitiveAssessment,
    sectionTitle = 'Puntos de Corte Ajustados para Evaluación Cognitiva',
    interpretationItems = [
        { label: '0 - 3 errores: Valoración cognitiva normal' },
        { label: '4 - 5 errores: Deterioro leve' },
        { label: '6 - 8 errores: Deterioro moderado' },
        { label: '9 - 10 errores: Deterioro severo' },
    ],
    educationLevel,
    evaluationDate,
    onComputedResult,
    register,
    scale,
}) => {
    const derived = React.useMemo(
        () => computeSpmsqResult(register, scale, educationLevel),
        [register, scale, educationLevel]
    );
    React.useEffect(() => {
            // Usamos onComputedResult, que es lo que recibimos por props
            if (derived && typeof onComputedResult === 'function') {
                onComputedResult(derived); 
            }
        }, [derived, onComputedResult]);
    const displayErrors = toNumberOrNull(totalErrors) ?? derived?.totalErrors ?? 0;
    const displayAssessment = cognitiveAssessment || derived?.cognitiveAssessment;
    const displayInterpretation = derived?.interpretationItems || interpretationItems;
    const educationLabel = educationLevel || derived?.educationLevel;
    

    return (
        <Paper elevation={0} style={{ padding: 24, borderRadius: 12 }}>
            <Typography variant="h5" style={{ fontWeight: 700, marginBottom: 12, color: '#0d1b2a' }}>
                {title}
            </Typography>

            <Typography variant="subtitle1" style={{ fontWeight: 600, color: '#0d1b2a' }}>
                Errores Totales: <span style={{ fontWeight: 400 }}>{displayErrors}/{maxErrors}</span>
            </Typography>

            {displayAssessment && (
                <Typography variant="subtitle1" style={{ fontWeight: 600, color: '#0d1b2a', marginTop: 12 }}>
                    Valoración Cognitiva: <span style={{ fontWeight: 400 }}>{displayAssessment}</span>
                </Typography>
            )}

            {educationLabel && (
                <Typography variant="body2" style={{ color: '#0d1b2a', marginTop: 8 }}>
                    Nivel educativo detectado: {educationLabel}
                </Typography>
            )}            

            <Typography variant="h6" style={{ fontWeight: 700, marginTop: 24, color: '#0d1b2a' }}>
                {sectionTitle}
            </Typography>

            <List style={{ paddingTop: 8 }}>
                {displayInterpretation.map((item, index) => (
                    <ListItem key={item?.label ?? index} disableGutters>
                        <ListItemIcon style={{ minWidth: 24 }}>
                            <FiberManualRecordIcon fontSize="small" style={{ color: '#0d1b2a' }} />
                        </ListItemIcon>
                        <ListItemText
                            primaryTypographyProps={{ style: { color: '#0d1b2a' } }}
                            primary={item?.label || ''}
                        />
                    </ListItem>
                ))}
            </List>
        </Paper>
    );
};

export default ScaleResultSPMSQ;
